import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import toast from "react-hot-toast";
import axiosClient from "../../axiosClient";
import AuthenticatedCheckoutForm from "../CheckoutForm/AuthenticatedCheckoutForm";
import CheckoutSkeleton from "../CheckoutForm/CheckoutSkeleton";
import Checkmark from "../Checkmark/Checkmark";
import { CouponSection, ProductPicker, getPurchasableProducts, useCoupon } from "../ProductPicker/ProductPicker";
import { useUser } from "../../context/UserContext";
import { brand, contactEmail, contactMailto } from "../../brand";
import poweredByStripe from "../../assets/poweredByStripe.svg";
import PRODUCTS from "../../pages/SignUpPage/products";
import {
  findProductForClassName,
  getOwnedProductIds,
  getRenewableProductIds,
  getTrialProductIds,
  withTrialProductFirst,
} from "../../lib/productAccess";
import "../../pages/SignUpPage/SignUpPage.scss";
import "./UpgradeModal.scss";

// Shared across mounts so Stripe.js is fetched once per page load, however often the modal opens.
let stripeRequest: Promise<Stripe | null> | null = null;
let loadedStripe: Stripe | null = null;

function loadCheckoutStripe(): Promise<Stripe | null> {
  if (!stripeRequest) {
    stripeRequest = axiosClient
      .get("payments/config")
      .then((r) => loadStripe(r.data.publishableKey))
      .then((instance) => {
        loadedStripe = instance;
        return instance;
      });
    stripeRequest.catch(() => {
      stripeRequest = null;
    });
  }
  return stripeRequest;
}

/** Fallback for browsers without requestIdleCallback (Safari). */
export const PREMOUNT_FALLBACK_DELAY_MS = 1500;

function whenIdle(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(callback, { timeout: PREMOUNT_FALLBACK_DELAY_MS * 2 });
    return () => window.cancelIdleCallback(handle);
  }
  const timeout = window.setTimeout(callback, PREMOUNT_FALLBACK_DELAY_MS);
  return () => window.clearTimeout(timeout);
}

/**
 * In-app checkout for logged-in users: free-trial upgrades, renewals after expiry and adding banks.
 * Students get it built in the background and it stays mounted once built, so opening it is only a CSS
 * transition and Stripe's card iframe is never reloaded.
 */
const UpgradeModal = ({ show, onHide }: { show: boolean; onHide: () => void }) => {
  const { user, accessStatus, trial, isOnTrial, hasTrialEnded, enrolments, fetchEnrolments } = useUser();
  const navigate = useNavigate();
  const purchasableProducts = getPurchasableProducts(PRODUCTS);
  // The session trial reaches "ended" as soon as the last question is answered or time runs out.
  const sessionTrial = useMemo(
    () => (trial ? { className: trial.className, ended: trial.ended || hasTrialEnded } : null),
    [trial, hasTrialEnded],
  );
  const ownedProductIds = useMemo(
    () => getOwnedProductIds(purchasableProducts, enrolments),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enrolments],
  );
  const { trialling: triallingProductIds, trialled: trialledProductIds } = useMemo(
    () => getTrialProductIds(purchasableProducts, enrolments, sessionTrial),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enrolments, sessionTrial],
  );
  const orderedProducts = useMemo(
    () => withTrialProductFirst(purchasableProducts, sessionTrial),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionTrial],
  );
  const renewableProductIds = useMemo(
    () => getRenewableProductIds(purchasableProducts, enrolments),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enrolments],
  );
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const userChangedSelection = useRef(false);
  const hasFetchedEnrolments = useRef(false);
  const isLoadingStripe = useRef(false);
  const showRef = useRef(show);
  showRef.current = show;
  const [stripe, setStripe] = useState<Stripe | null>(loadedStripe);
  const [isMounted, setIsMounted] = useState(show);
  // Trails `show` by a frame so the opening transition always runs, even when the modal mounts already open.
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const pressStartedOutside = useRef(false);
  const onHideRef = useRef(onHide);
  onHideRef.current = onHide;
  const isStudent = user?.type === "student";

  let heading = "Add question banks";
  let intro = "Choose the question banks you want full access to.";
  if (hasTrialEnded) {
    heading = "Your free trial has ended";
    intro = "Thanks for trying us out. Choose a question bank below to keep practising with full access.";
  } else if (isOnTrial) {
    heading = "Upgrade to full access";
    intro = "Unlock every question, module practice, timed exams, starred questions and detailed statistics.";
  } else if (accessStatus === "expired") {
    heading = "Your access has expired";
    intro = "Your question bank subscription has ended. Renew below to pick up where you left off.";
  }

  const coupon = useCoupon({
    products: PRODUCTS,
    selectedProducts,
    getEmail: () => user?.email ?? user?.username ?? "",
  });
  const { appliedCoupon, totalPrice } = coupon;

  useEffect(() => {
    if (show) {
      setIsMounted(true);
      return;
    }
    if (isMounted || !isStudent) {
      return;
    }
    return whenIdle(() => setIsMounted(true));
  }, [show, isMounted, isStudent]);

  useEffect(() => {
    if (!show) {
      setIsOpen(false);
      return;
    }
    let frame = window.requestAnimationFrame(() => {
      frame = window.requestAnimationFrame(() => setIsOpen(true));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [show]);

  // Lock the html element rather than body so react-bootstrap modals restoring body styles can't unlock it.
  useEffect(() => {
    if (!show) {
      return;
    }
    const html = document.documentElement;
    const previous = { overflow: html.style.overflow, paddingRight: html.style.paddingRight };
    const scrollbarWidth = window.innerWidth - html.clientWidth;
    html.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      html.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      html.style.overflow = previous.overflow;
      html.style.paddingRight = previous.paddingRight;
    };
  }, [show]);

  useEffect(() => {
    if (!show) {
      return;
    }
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onHideRef.current();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocused?.focus?.({ preventScroll: true });
    };
  }, [show]);

  // Focus can only move in once the dialog is visible; other dialogs and toasts keep their own focus.
  useEffect(() => {
    const root = rootRef.current;
    if (!isOpen || !root) {
      return;
    }
    root.focus({ preventScroll: true });
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      // `isOpen` trails `show`, so check `show` too or closing would trap the focus being handed back.
      if (!showRef.current || !target || root.contains(target) || target.closest?.('[role="dialog"], [aria-live]')) {
        return;
      }
      root.focus({ preventScroll: true });
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, [isOpen]);

  useEffect(() => {
    if (!isMounted || hasFetchedEnrolments.current) {
      return;
    }
    hasFetchedEnrolments.current = true;
    if (isStudent && enrolments.length === 0) {
      fetchEnrolments().catch((err) => console.error(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  // A failed background warm-up stays quiet; opening the modal retries and reports the failure.
  useEffect(() => {
    if (!isMounted || stripe || isLoadingStripe.current) {
      return;
    }
    isLoadingStripe.current = true;
    loadCheckoutStripe()
      .then(setStripe)
      .catch((err) => {
        console.error(err);
        if (showRef.current) {
          toast.error("Error occured whilst loading checkout. Please seek support!");
        }
      })
      .finally(() => {
        isLoadingStripe.current = false;
      });
  }, [isMounted, show, stripe]);

  // Pre-select the bank the user trialled (or previously owned) until they pick for themselves.
  useEffect(() => {
    if (userChangedSelection.current) {
      return;
    }
    const classNames = trial
      ? [trial.className]
      : enrolments.filter((e) => e.isActive === false).map((e) => e.className);
    const ids = new Set<string>();
    classNames.forEach((className) => {
      const product = findProductForClassName(purchasableProducts, className);
      if (product && !ownedProductIds.has(product.id)) {
        ids.add(product.id);
      }
    });
    if (ids.size > 0) {
      setSelectedProducts(ids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trial, enrolments, ownedProductIds]);

  // Enrolments can arrive after a pick, so drop anything that has turned out to be owned.
  useEffect(() => {
    setSelectedProducts((current) => {
      if (!Array.from(current).some((id) => ownedProductIds.has(id))) {
        return current;
      }
      return new Set(Array.from(current).filter((id) => !ownedProductIds.has(id)));
    });
  }, [ownedProductIds]);

  const handleSelectionChange = (next: Set<string>) => {
    userChangedSelection.current = true;
    setSelectedProducts(new Set(Array.from(next).filter((id) => !ownedProductIds.has(id))));
  };

  const handlePaymentComplete = () => {
    toast.success("Payment successful. Welcome aboard!");
    // Full reload so access status, trial and enrolments are re-read from the server.
    window.location.assign("/dashboard");
  };

  const reviewTrialAnswers = () => {
    if (!trial) {
      return;
    }
    onHide();
    navigate(`/review?subjectId=${trial.subjectId}`);
  };

  const subtotal = totalPrice();

  if (!isMounted) {
    return null;
  }

  const isOutsideContent = (target: EventTarget) => !contentRef.current?.contains(target as Node);

  return createPortal(
    <>
      <div className={`modal-backdrop fade upgrade-modal-backdrop${isOpen ? " show" : ""}`} />
      <div
        ref={rootRef}
        className={`modal fade upgrade-modal-root${isOpen ? " show" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
        aria-hidden={!show}
        tabIndex={-1}
        onMouseDown={(e) => {
          pressStartedOutside.current = isOutsideContent(e.target);
        }}
        onClick={(e) => {
          if (show && pressStartedOutside.current && isOutsideContent(e.target)) {
            onHide();
          }
        }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable upgrade-modal-dialog">
          <div className="modal-content" ref={contentRef}>
            <div className="modal-header upgrade-modal__header">
              <div>
                <div className="modal-title h4" id="upgrade-modal-title">{heading}</div>
                <p className="upgrade-modal__intro">{intro}</p>
                {hasTrialEnded && trial && trial.used > 0 && (
                  <button type="button" className="upgrade-modal__review" onClick={reviewTrialAnswers}>
                    Not yet? Review your {trial.used} trial answer{trial.used === 1 ? "" : "s"} →
                  </button>
                )}
              </div>
              <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
            </div>
            <div className="modal-body upgrade-modal__body">
              <div id="SignUpPage" className="upgrade-modal">
                <div className="inner-cols-wrapper">
                  <div className="order-summary-col">
                    <form id="form-wrapper" onSubmit={(e) => e.preventDefault()}>
                      {trial && (
                        <p className="signup-form-hint upgrade-modal__trial-note">
                          Your progress on your {trial.used} trial question{trial.used === 1 ? "" : "s"} is kept
                          when you upgrade.
                        </p>
                      )}

                      <h5 className="signup-form-heading signup-form-heading--tight">Products</h5>
                      <ProductPicker
                        products={orderedProducts}
                        selected={selectedProducts}
                        onChange={handleSelectionChange}
                        ownedProductIds={ownedProductIds}
                        triallingProductIds={triallingProductIds}
                        trialledProductIds={trialledProductIds}
                        renewableProductIds={renewableProductIds}
                        multiple
                      />
                      {selectedProducts.size > 0 && (
                        <div id="totalPrice" className="signup-total-block">
                          {!appliedCoupon && (
                            <h2 className="signup-total-heading">
                              Total: <span className="signup-total-amount">£{subtotal}</span>{" "}
                              <span className="signup-total-note">due today</span>
                            </h2>
                          )}
                          {appliedCoupon && (
                            <div>
                              <div className="signup-total-row">
                                <span>Subtotal</span>
                                <span>£{subtotal}</span>
                              </div>
                              <div className="signup-total-row signup-total-row--discount">
                                <span>Coupon ({appliedCoupon.code})</span>
                                <span>-£{appliedCoupon.discountAmount}</span>
                              </div>
                              <h2 className="signup-total-heading">
                                Total: <span className="signup-total-amount">£{appliedCoupon.totalAfter}</span>{" "}
                                <span className="signup-total-note">due today</span>
                              </h2>
                            </div>
                          )}
                        </div>
                      )}

                      {selectedProducts.size > 0 && <CouponSection coupon={coupon} />}

                      <h4 className="order-summary-section-title">Billing Details</h4>
                      <div className="billing-details-section">
                        {!stripe && <CheckoutSkeleton />}
                        {stripe && (
                          <Elements
                            stripe={stripe}
                            options={{
                              mode: "subscription",
                              amount: 10000,
                              currency: "gbp",
                              payment_method_types: ["card"],
                            }}
                          >
                            <AuthenticatedCheckoutForm
                              products={Array.from(selectedProducts)}
                              couponCode={appliedCoupon?.code}
                              onComplete={handlePaymentComplete}
                              onAlreadyOwned={() => fetchEnrolments().catch((err) => console.error(err))}
                            />
                          </Elements>
                        )}
                      </div>
                    </form>
                  </div>

                  <div className="key-features-col">
                    <h3 className="key-features-title">Key Features</h3>
                    <div className="key-features-list-wrapper">
                      <ul>
                        {brand.signupKeyFeatures.map((feature) => (
                          <li key={feature}>
                            <Checkmark />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <img src={poweredByStripe} alt="Powered by Stripe.com" id="powered-by-stripe" />
                    <p id="please-contact-us">
                      Please contact <a href={contactMailto}>{contactEmail}</a> for any support
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default UpgradeModal;
